import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useDetailedCreditsUsage } from '@/hooks/useDetailedCreditsUsage';
import { MessageSquare, Sparkles, Calendar, Coins, Plus, Clock } from 'lucide-react';
import { formatNumber } from '@/utils/statisticsFormatters';
import { useNavigate } from 'react-router-dom';

interface CreditUsageDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreditUsageDetailsDialog: React.FC<CreditUsageDetailsDialogProps> = ({ open, onOpenChange }) => {
  const { data, isLoading } = useDetailedCreditsUsage();
  const navigate = useNavigate();

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getMonthName = (monthYear: string) => {
    const [year, month] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const handleBuyCredits = () => {
    onOpenChange(false);
    navigate('/planos');
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data) {
    return null;
  }

  const usagePercentage = data.limit > 0 ? (data.totalUsed / data.limit) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Coins className="h-5 w-5 text-primary" />
            Detalhes de Uso - Créditos
          </DialogTitle>
          <DialogDescription>
            Visualização detalhada do consumo de créditos
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Ciclo Atual */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Calendar className="h-4 w-4" />
              Ciclo Atual
            </div>
            <div className="rounded-lg bg-muted/50 p-3 space-y-1">
              <div className="font-semibold capitalize">{getMonthName(data.monthYear)}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Renovação: {formatDate(data.renewalDate)}
              </div>
            </div>
          </div>

          {/* Consumo Detalhado */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">📊 Consumo Detalhado</div>
            
            {/* Input (Perguntas) */}
            <div className="rounded-lg border bg-card p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">Perguntas (Input)</span>
                </div>
                <span className="text-sm font-semibold">{formatNumber(data.inputCredits)}</span>
              </div>
              <Progress value={data.inputPercentage} className="h-2" />
              <div className="text-xs text-muted-foreground text-right">
                {data.inputPercentage.toFixed(1)}% do total usado
              </div>
            </div>

            {/* Output (Respostas) */}
            <div className="rounded-lg border bg-card p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">Respostas (Output)</span>
                </div>
                <span className="text-sm font-semibold">{formatNumber(data.outputCredits)}</span>
              </div>
              <Progress value={data.outputPercentage} className="h-2" />
              <div className="text-xs text-muted-foreground text-right">
                {data.outputPercentage.toFixed(1)}% do total usado
              </div>
            </div>

            {/* Total */}
            <div className="rounded-lg bg-muted p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Total Usado</span>
                <span className="font-semibold">
                  {formatNumber(data.totalUsed)} / {formatNumber(data.limit)}
                </span>
              </div>
              <Progress value={usagePercentage} className="h-2 mt-2" />
            </div>
          </div>

          {/* Créditos Extras */}
          {data.purchasedCredits.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Coins className="h-4 w-4" />
                💎 Créditos Extras
              </div>
              <div className="space-y-2">
                {data.purchasedCredits.map((pc, idx) => (
                  <div key={idx} className="rounded-lg border bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-950/20 dark:to-amber-950/20 p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <div className="font-medium">{formatNumber(pc.remaining)} créditos</div>
                        <div className="text-xs text-muted-foreground">
                          de {formatNumber(pc.amount)} originais
                        </div>
                      </div>
                      <div className="text-right text-xs">
                        <div className="text-muted-foreground">expira em</div>
                        <div className="font-semibold">{pc.daysUntilExpiry}d</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botão de Compra */}
          <Button 
            variant="outline" 
            className="w-full border-primary/20 hover:bg-primary/5 hover:border-primary/30 transition-colors"
            onClick={handleBuyCredits}
          >
            <Plus className="w-4 h-4 mr-2" />
            Comprar Mais Créditos
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
